import { useState, useMemo, useEffect, useRef, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";
import {
  Search,
  Filter,
  Shield,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  FileText,
  DollarSign,
  Calendar as CalendarIcon,
  User,
  Plane,
  X,
  Sparkles,
  Mail,
  Phone,
  MoreVertical,
  RotateCcw,
} from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO, isAfter, isBefore } from "date-fns";
import purchaseService from "../../api/PurchaseService";
import policyService from "../../api/PolicyService";
import Loader from "../../components/Loader";
import ConfirmCancelModal from "../../components/ConfirmCancelModal";
import RevertCancelModal from "../../components/RevertCancelModal";
import { toast } from "react-toastify";
import useAccountStatusCheck from "../../hooks/useAccountStatusCheck";

const columnHelper = createColumnHelper();

const MENU_WIDTH = 220;
const MENU_GAP = 8;
const VIEWPORT_PAD = 12;

const placeMenu = (button, menuHeight) => {
  const rect = button.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_PAD;
  const spaceAbove = rect.top - VIEWPORT_PAD;
  const openUp = spaceBelow < menuHeight + MENU_GAP && spaceAbove > spaceBelow;
  const left = Math.max(
    VIEWPORT_PAD,
    Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - VIEWPORT_PAD)
  );
  const rawTop = openUp ? rect.top - MENU_GAP - menuHeight : rect.bottom + MENU_GAP;
  const top = Math.max(VIEWPORT_PAD, Math.min(rawTop, window.innerHeight - menuHeight - VIEWPORT_PAD));
  return { top, left, hidden: rect.bottom < 0 || rect.top > window.innerHeight };
};

const PurchaseActionsMenu = ({ onSelect }) => {
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const items = [
    { id: "finalize", label: "Cancel policy", icon: XCircle, tone: "text-red-700 hover:bg-red-50" },
    { id: "revert", label: "Revert cancellation", icon: RotateCcw, tone: "text-emerald-800 hover:bg-emerald-50" },
  ];

  const syncPosition = useCallback(() => {
    const button = buttonRef.current;
    if (!button) return false;
    const height = menuRef.current?.offsetHeight || 96;
    const next = placeMenu(button, height);
    if (next.hidden) return false;
    setCoords(next);
    return true;
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    if (!syncPosition()) setOpen(false);
    const onScroll = () => {
      if (!syncPosition()) setOpen(false);
    };
    window.addEventListener("resize", onScroll);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("resize", onScroll);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open, syncPosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (buttonRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label="Actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          if (open) {
            setOpen(false);
            return;
          }
          const button = buttonRef.current;
          if (!button) return;
          setCoords(placeMenu(button, 96));
          setOpen(true);
        }}
        className={`inline-flex h-9 w-9 items-center justify-center rounded-lg border shadow-sm ${
          open
            ? "border-slate-300 bg-slate-50 text-slate-800"
            : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
        }`}
      >
        <MoreVertical className="h-4 w-4" />
      </button>
      {open &&
        coords &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{ position: "fixed", top: coords.top, left: coords.left, width: MENU_WIDTH, zIndex: 40 }}
            className="rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl"
          >
            {items.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    onSelect(item.id);
                  }}
                  className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm font-medium ${item.tone}`}
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
};

const StatusBadge = ({ status }) => {
  const configs = {
    active: {
      icon: CheckCircle,
      color: "bg-green-100 text-green-800 border-green-200",
      label: "Active",
    },
    expired: {
      icon: Clock,
      color: "bg-yellow-100 text-yellow-800 border-yellow-200",
      label: "Expired",
    },
    cancelled: {
      icon: XCircle,
      color: "bg-red-100 text-red-800 border-red-200",
      label: "Cancelled",
    },
    cancel_pending: {
      icon: AlertCircle,
      color: "bg-yellow-100 text-yellow-800 border-yellow-200",
      label: "Cancel Pending",
    },
    pending: {
      icon: AlertCircle,
      color: "bg-blue-100 text-blue-800 border-blue-200",
      label: "Pending",
    },
  };

  const config = configs[status] || configs.pending;
  const Icon = config.icon;

  return (
    <motion.span
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${config.color}`}
    >
      <Icon className="mr-1 w-3 h-3" />
      {config.label}
    </motion.span>
  );
};


const AmountCell = ({ amount }) => {
  const formattedAmount = typeof amount === 'number' 
    ? amount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : parseFloat(amount || 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

  return (
    <motion.div
      initial={{ x: -10, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      className="font-semibold text-gray-900"
    >
      ₵{formattedAmount}
    </motion.div>
  );
};

const PlanBadge = ({ plan, insuranceType }) => {
  const getColorAndIcon = () => {
    if (insuranceType === "travel_insurance") {
      const travelColors = {
        "Intercity Travel": "bg-blue-100 text-blue-800 border-blue-200",
        "Daily Commuter": "bg-emerald-100 text-emerald-800 border-emerald-200",
        "Motor Rider": "bg-orange-100 text-orange-800 border-orange-200",
        "Taxi & Bolt": "bg-purple-100 text-purple-800 border-purple-200",
      };
      return {
        color: travelColors[plan] || travelColors["Intercity Travel"],
        icon: Plane,
      };
    } else if (insuranceType === "life_insurance") {
      return {
        color: "bg-indigo-100 text-indigo-800 border-indigo-200",
        icon: Shield,
      };
    } else if (insuranceType === "general_insurance") {
      return {
        color: "bg-purple-100 text-purple-800 border-purple-200",
        icon: FileText,
      };
    }
    return {
      color: "bg-gray-100 text-gray-800 border-gray-200",
      icon: Shield,
    };
  };

  const { color, icon: Icon } = getColorAndIcon();

  return (
    <span
      className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md border ${color}`}
    >
      <Icon className="mr-1 w-3 h-3" />
      {plan}
    </span>
  );
};

export default function PurchaseManagement() {
  const { isBlocked, AccountStatusModal } = useAccountStatusCheck();
  const [globalFilter, setGlobalFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [planFilter, setPlanFilter] = useState("all");
  const [insuranceTypeFilter, setInsuranceTypeFilter] = useState("all");
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [showFilters, setShowFilters] = useState(false);
  const [cancelAction, setCancelAction] = useState(null);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      globalFilter !== "" ||
      statusFilter !== "all" ||
      planFilter !== "all" ||
      insuranceTypeFilter !== "all" ||
      startDate !== null ||
      endDate !== null
    );
  }, [globalFilter, statusFilter, planFilter, insuranceTypeFilter, startDate, endDate]);

  // Reset all filters
  const resetFilters = () => {
    setGlobalFilter("");
    setStatusFilter("all");
    setPlanFilter("all");
    setInsuranceTypeFilter("all");
    setDateRange([null, null]);
  };
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);

  // Transform raw purchase data to table format
  const transformPurchaseData = useMemo(() => {
    return purchases.map((purchase) => {
      const purchasedData = purchase.purchased_policy_data || {};
      const customer = purchase.customer || {};
      
      // Determine insurance type: if tripType exists, it's travel insurance
      const insuranceType = purchasedData?.insurance_type || 
                           (purchasedData?.tripType ? "travel_insurance" : 
                           (purchasedData?.coverageDuration ? "travel_insurance" : "unknown"));
      
      // Determine policy type and plan name
      let planName = "Unknown";
      let policyType = purchasedData?.policy_type;
      
      if (insuranceType === "travel_insurance") {
        const typeMap = {
          intercity_travel: "Intercity Travel",
          daily_commuter: "Daily Commuter",
          okada_rider: "Motor Rider",
          taxi_and_bolt: "Taxi & Bolt",
        };
        const basePlanName = typeMap[policyType] || "Travel Insurance";
        
        // Enhance plan name with route information from coverageDuration
        if (purchasedData?.coverageDuration) {
          const coverageDuration = purchasedData.coverageDuration;
          const routeDestination = coverageDuration.route_destination || purchasedData.routeDestination;
          const displayValue = coverageDuration.display_value || coverageDuration.hours;
          const displayUnit = coverageDuration.display_unit || "hours";
          
          if (routeDestination) {
            planName = `${basePlanName} - ${routeDestination}`;
          } else if (displayValue) {
            planName = `${basePlanName} (${displayValue} ${displayUnit})`;
          } else {
            planName = basePlanName;
          }
        } else {
          planName = basePlanName;
        }
      } else if (insuranceType === "life_insurance") {
        planName = purchasedData?.product_name || "Life Insurance";
      } else if (insuranceType === "general_insurance") {
        const subcategoryMap = {
          health_insurance: "Health Insurance",
          home_insurance: "Home Insurance",
          accident_insurance: "Accident Insurance",
          fire_insurance: "Fire Insurance",
          marine_insurance: "Marine Insurance",
          liability_insurance: "Liability Insurance",
        };
        planName = purchasedData?.product_name || subcategoryMap[policyType] || "General Insurance";
      }

      // Get amount
      let amount = 0;
      if (insuranceType === "travel_insurance" && purchasedData?.coverageDuration) {
        amount = purchasedData.coverageDuration.price || 0;
      } else if (insuranceType === "life_insurance" || insuranceType === "general_insurance") {
        amount = purchasedData?.premium || 0;
      }

      // Get status
      const status =
        purchase.policy_status === null ||
        purchase.policy_status === "active"
          ? "active"
          : purchase.policy_status === "inactive"
          ? "cancelled"
          : purchase.policy_status || "pending";

      const purchasedAt =
        purchasedData?.coverageDuration?.purchased_at ||
        purchasedData?.purchased_at ||
        purchase.policy_start_date ||
        null;
      const purchaseDate = purchasedAt ? new Date(purchasedAt).toISOString() : null;

      return {
        id: purchase.insurance_policy_slug || "N/A",
        purchaseSlug: purchase.slug,
        purchaseRecordId: purchase.id,
        customerName: `${customer.first_name || ""} ${customer.last_name || ""}`.trim() || "Unknown",
        customerEmail: customer.email || "N/A",
        customerPhone: customer.phone_number || "N/A",
        insurancePlan: planName,
        insuranceType: insuranceType,
        amount: amount,
        purchaseDate: purchaseDate,
        status: status,// Keep raw data for reference
      };
    });
  }, [purchases]);

  // Generate data for table
  const data = useMemo(() => transformPurchaseData, [transformPurchaseData]);

  useEffect(() => {
    const fetchPurchases = async () => {
      setLoading(true);
      try {
        const response = await purchaseService.getPurchases();
        if (response.status && response.data?.status_code === 200) {
          const payload = response.data.data;
          const rawPurchases = Array.isArray(payload)
            ? payload
            : payload && typeof payload === "object"
              ? Object.values(payload)
              : [];
          
          // Optionally fetch policy details for each purchase
          const enrichedPurchases = await Promise.all(
            rawPurchases.map(async (purchase) => {
              try {
                const policyDetails = await policyService.getPolicy(
                  purchase.insurance_policy_slug
                );
                
                if (policyDetails.status && policyDetails.data?.data) {
                  return {
                    ...purchase,
                    insurance_policy: policyDetails.data.data,
                  };
                }
                return purchase;
              } catch (error) {
                console.error(
                  `Failed to fetch policy details for ${purchase.insurance_policy_slug}:`,
                  error
                );
                return purchase;
              }
            })
          );
          
          setPurchases(enrichedPurchases);
        } else {
          toast.error(
            response.message || "An error occurred while fetching purchases"
          );
        }
      } catch (error) {
        toast.error(
          error.response?.data?.reason ||
            "An error occurred while fetching purchases"
        );
        setPurchases([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPurchases();
  }, []);

  const applyPurchaseStatus = (purchaseSlug, policyStatus) => {
    setPurchases((list) =>
      list.map((purchase) =>
        purchase.slug === purchaseSlug
          ? { ...purchase, policy_status: policyStatus }
          : purchase
      )
    );
  };

  const handleCancelDecision = async () => {
    if (!cancelAction?.purchaseSlug || cancelSubmitting) return;
    const { purchaseSlug, action } = cancelAction;
    setCancelSubmitting(true);
    try {
      const response =
        action === "revert"
          ? await purchaseService.revertCancellation(purchaseSlug)
          : await purchaseService.cancelPurchase(purchaseSlug);

      if (!response.status) {
        toast.error(response.message || "Could not update this cancellation");
        return;
      }

      applyPurchaseStatus(purchaseSlug, action === "revert" ? "active" : "inactive");
      toast.success(
        action === "revert"
          ? "Cancellation reverted. The policy stays active."
          : "Policy cancelled."
      );
      setCancelAction(null);
    } catch (error) {
      toast.error(error?.message || "Could not update this cancellation");
    } finally {
      setCancelSubmitting(false);
    }
  };

  const columns = useMemo(() => {
    const baseColumns = [
      columnHelper.accessor("id", {
        header: ({ column }) => (
          <button
            className="flex items-center space-x-1 font-semibold text-gray-700 whitespace-nowrap hover:text-gray-900"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <FileText className="w-4 h-4" />
            <span>Policy ID</span>
            {column.getIsSorted() === "asc" ? (
              <ArrowUp className="w-4 h-4" />
            ) : column.getIsSorted() === "desc" ? (
              <ArrowDown className="w-4 h-4" />
            ) : (
              <ArrowUpDown className="w-4 h-4" />
            )}
          </button>
        ),
        cell: ({ getValue }) => {
          const fullId = getValue();
          const shortenedId = fullId && fullId !== "N/A" && fullId.length > 20
            ? `${fullId.substring(0, 20)}...`
            : fullId;
          
          return (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="relative font-mono text-sm font-medium text-blue-600 cursor-help group"
              title={fullId && fullId !== "N/A" ? fullId : undefined}
            >
              <span className="truncate block max-w-[200px]">{shortenedId}</span>
              {fullId && fullId !== "N/A" && fullId.length > 20 && (
                <div className="hidden absolute left-0 bottom-full z-50 px-3 py-2 mb-2 max-w-xs text-xs text-white break-all bg-gray-900 rounded-lg shadow-lg group-hover:block">
                  <div className="font-mono">{fullId}</div>
                  <div className="absolute left-4 top-full w-0 h-0 border-t-4 border-r-4 border-l-4 border-transparent border-t-gray-900"></div>
                </div>
              )}
            </motion.div>
          );
        },
      }),

      columnHelper.accessor("customerName", {
        header: ({ column }) => (
          <button
            className="flex items-center space-x-1 font-semibold text-gray-700 whitespace-nowrap hover:text-gray-900"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <User className="w-4 h-4" />
            <span>Customer Name</span>
            {column.getIsSorted() === "asc" ? (
              <ArrowUp className="w-4 h-4" />
            ) : column.getIsSorted() === "desc" ? (
              <ArrowDown className="w-4 h-4" />
            ) : (
              <ArrowUpDown className="w-4 h-4" />
            )}
          </button>
        ),
        cell: ({ getValue }) => (
          <motion.div
            initial={{ x: -10, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            className="font-medium text-gray-900"
          >
            {getValue()}
          </motion.div>
        ),
      }),

      columnHelper.accessor("customerEmail", {
        header: ({ column }) => (
          <button
            className="flex items-center space-x-1 font-semibold text-gray-700 whitespace-nowrap hover:text-gray-900"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <Mail className="w-4 h-4" />
            <span>Email</span>
            {column.getIsSorted() === "asc" ? (
              <ArrowUp className="w-4 h-4" />
            ) : column.getIsSorted() === "desc" ? (
              <ArrowDown className="w-4 h-4" />
            ) : (
              <ArrowUpDown className="w-4 h-4" />
            )}
          </button>
        ),
        cell: ({ getValue }) => (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-sm text-gray-700 truncate max-w-[200px]"
            title={getValue()}
          >
            {getValue()}
          </motion.div>
        ),
      }),

      columnHelper.accessor("customerPhone", {
        header: ({ column }) => (
          <button
            className="flex items-center space-x-1 font-semibold text-gray-700 whitespace-nowrap hover:text-gray-900"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <Phone className="w-4 h-4" />
            <span>Phone</span>
            {column.getIsSorted() === "asc" ? (
              <ArrowUp className="w-4 h-4" />
            ) : column.getIsSorted() === "desc" ? (
              <ArrowDown className="w-4 h-4" />
            ) : (
              <ArrowUpDown className="w-4 h-4" />
            )}
          </button>
        ),
        cell: ({ getValue }) => {
          const phone = getValue();
          const formattedPhone = phone && phone !== "N/A" 
            ? phone.startsWith("+") 
              ? phone 
              : `+${phone}`
            : phone;
          
          return (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="font-mono text-sm text-gray-700"
            >
              {formattedPhone}
            </motion.div>
          );
        },
      }),

      columnHelper.accessor("insurancePlan", {
        header: ({ column }) => (
          <button
            className="flex items-center space-x-1 font-semibold text-gray-700 whitespace-nowrap hover:text-gray-900"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <Shield className="w-4 h-4" />
            <span>Insurance Plan</span>
            {column.getIsSorted() === "asc" ? (
              <ArrowUp className="w-4 h-4" />
            ) : column.getIsSorted() === "desc" ? (
              <ArrowDown className="w-4 h-4" />
            ) : (
              <ArrowUpDown className="w-4 h-4" />
            )}
          </button>
        ),
        cell: ({ row }) => (
          <PlanBadge 
            plan={row.original.insurancePlan} 
            insuranceType={row.original.insuranceType}
          />
        ),
      }),

      columnHelper.accessor("amount", {
        header: ({ column }) => (
          <button
            className="flex items-center space-x-1 font-semibold text-gray-700 whitespace-nowrap hover:text-gray-900"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <DollarSign className="w-4 h-4" />
            <span>Amount (₵)</span>
            {column.getIsSorted() === "asc" ? (
              <ArrowUp className="w-4 h-4" />
            ) : column.getIsSorted() === "desc" ? (
              <ArrowDown className="w-4 h-4" />
            ) : (
              <ArrowUpDown className="w-4 h-4" />
            )}
          </button>
        ),
        cell: ({ getValue }) => <AmountCell amount={getValue()} />,
      }),

      columnHelper.accessor("purchaseDate", {
        header: ({ column }) => (
          <button
            className="flex items-center space-x-1 font-semibold text-gray-700 whitespace-nowrap hover:text-gray-900"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <CalendarIcon className="w-4 h-4" />
            <span>Purchase Date</span>
            {column.getIsSorted() === "asc" ? (
              <ArrowUp className="w-4 h-4" />
            ) : column.getIsSorted() === "desc" ? (
              <ArrowDown className="w-4 h-4" />
            ) : (
              <ArrowUpDown className="w-4 h-4" />
            )}
          </button>
        ),
        cell: ({ getValue }) => {
          const value = getValue();
          if (!value) {
            return <span className="text-sm text-gray-400">—</span>;
          }
          const purchasedAt = parseISO(value);
          return (
            <span className="whitespace-nowrap text-sm text-gray-600">
              {format(purchasedAt, "MMM dd, yyyy, h:mm a")}
            </span>
          );
        },
      }),

      columnHelper.accessor("status", {
        header: () => (
          <span className="font-semibold text-gray-700 whitespace-nowrap">
            Status
          </span>
        ),
        cell: ({ getValue }) => <StatusBadge status={getValue()} />,
      }),

      columnHelper.display({
        id: "actions",
        header: () => (
          <span className="font-semibold text-gray-700 whitespace-nowrap">
            Actions
          </span>
        ),
        cell: ({ row }) => {
          if (row.original.status !== "cancel_pending") {
            return <span className="text-sm text-gray-400">—</span>;
          }
          return (
            <PurchaseActionsMenu
              onSelect={(action) =>
                setCancelAction({ action, ...row.original })
              }
            />
          );
        },
      }),
    ];

    return baseColumns;
  }, []);

  // Filter data
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const matchesGlobal =
        globalFilter === "" ||
        Object.values(item).some((value) =>
          value?.toString().toLowerCase().includes(globalFilter.toLowerCase())
        );

      const matchesStatus =
        statusFilter === "all" || item.status === statusFilter;
      const matchesPlan =
        planFilter === "all" || item.insurancePlan === planFilter;
      const matchesInsuranceType =
        insuranceTypeFilter === "all" || item.insuranceType === insuranceTypeFilter;

      const itemDate = item.purchaseDate ? parseISO(item.purchaseDate) : null;
      const matchesDateRange =
        (!startDate && !endDate) ||
        (itemDate &&
          (!startDate ||
            isAfter(itemDate, startDate) ||
            itemDate.toDateString() === startDate.toDateString()) &&
          (!endDate ||
            isBefore(itemDate, endDate) ||
            itemDate.toDateString() === endDate.toDateString()));

      return matchesGlobal && matchesStatus && matchesPlan && matchesInsuranceType && matchesDateRange;
    });
  }, [data, globalFilter, statusFilter, planFilter, insuranceTypeFilter, startDate, endDate]);

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  // Stats calculations
  const stats = useMemo(() => {
    const totalPurchases = filteredData.length;
    const totalAmount = filteredData.reduce(
      (sum, item) => sum + item.amount,
      0
    );
    const activePurchases = filteredData.filter(
      (item) => item.status === "active"
    ).length;
    const cancelledPurchases = filteredData.filter(
      (item) => item.status === "cancelled"
    ).length;

    return { totalPurchases, totalAmount, activePurchases, cancelledPurchases };
  }, [filteredData]);

  const uniquePlans = useMemo(() => {
    return [...new Set(data.map((item) => item.insurancePlan))];
  }, [data]);

  if (loading) {
    return <Loader fullScreen={true} text="Loading purchases..." />;
  }

  return (
    <div className="p-6 min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Account Status Modal */}
      <AccountStatusModal />

      <div className={`mx-auto max-w-full ${isBlocked ? 'opacity-30 blur-sm transition-all duration-300 pointer-events-none' : ''}`}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="mb-4 lg:mb-0">
              <h1 className="mb-2 text-3xl font-bold text-gray-900">
                Purchase Management
              </h1>
              <p className="text-gray-600">
                Monitor and manage all policy purchases with advanced filtering
                and analytics
              </p>
            </div>

          </div>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-2 lg:grid-cols-4"
        >
          <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Total Purchases
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.totalPurchases.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Total Revenue
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  ₵{stats.totalAmount.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Active
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.activePurchases.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Cancelled Policies
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.cancelledPurchases.toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-col gap-4 mb-6 sm:flex-row sm:items-center sm:justify-between"
        >
          {/* Search Input */}
          <div className="relative flex-1 max-w-md">
            <div className="flex absolute inset-y-0 left-0 items-center pl-4 pointer-events-none">
              <Search className={`w-5 h-5 transition-colors ${
                globalFilter ? "text-blue-600" : "text-gray-400"
              }`} />
            </div>
            <input
              type="text"
              placeholder="Search by customer, policy ID, plan..."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="pr-12 pl-12 w-full h-12 text-sm font-medium placeholder-gray-400 text-gray-900 bg-white rounded-xl border-2 border-gray-200 shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-300 hover:shadow-md"
            />
            {globalFilter && (
              <button
                onClick={() => setGlobalFilter("")}
                className="flex absolute inset-y-0 right-0 items-center pr-4 text-gray-400 transition-colors hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Filter Button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowFilters(true)}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold rounded-xl transition-all duration-200 shadow-sm hover:shadow-md ${
              hasActiveFilters
                ? "text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                : "text-gray-700 bg-white border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50"
            }`}
          >
            <Filter className="w-5 h-5" />
            <span>Filters</span>
            {hasActiveFilters && (
              <span className="px-2 py-0.5 text-xs font-bold bg-white/30 rounded-full">
                {[
                  globalFilter && 1,
                  insuranceTypeFilter !== "all" && 1,
                  statusFilter !== "all" && 1,
                  planFilter !== "all" && 1,
                  (startDate || endDate) && 1,
                ].filter(Boolean).length}
              </span>
            )}
          </motion.button>
        </motion.div>

        {/* Filter Drawer */}
        <AnimatePresence>
          {showFilters && (
            <>
              {/* Overlay */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 z-40 backdrop-blur-sm bg-black/50"
                onClick={() => setShowFilters(false)}
              />

              {/* Drawer */}
              <motion.div
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "spring", damping: 25, stiffness: 200 }}
                className="flex fixed top-0 right-0 z-50 flex-col w-full max-w-md h-full bg-white shadow-2xl"
              >
                {/* Drawer Header */}
                <div className="p-6 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 border-b border-gray-200">
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex gap-3 items-center">
                      <div className="grid place-items-center w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-md">
                        <Sparkles className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">
                          Filters
                        </h3>
                        <p className="text-xs text-gray-600">
                          Refine your search
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowFilters(false)}
                      className="grid place-items-center w-9 h-9 text-gray-500 rounded-lg transition-colors hover:text-gray-700 hover:bg-white/50"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {hasActiveFilters && (
                    <motion.button
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={resetFilters}
                      className="flex gap-2 items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-200 transition-all hover:bg-gray-50 hover:shadow-sm"
                    >
                      <X className="w-4 h-4" />
                      <span>Clear All Filters</span>
                    </motion.button>
                  )}
                </div>

                {/* Drawer Content */}
                <div className="overflow-y-auto flex-1 p-6">
                  <div className="space-y-6">
                    {/* Insurance Type Filter */}
                    <div className="relative group">
                      <label className="block mb-3 text-sm font-semibold text-gray-900">
                        <div className="flex gap-2 items-center">
                          <Shield className={`w-4 h-4 ${
                            insuranceTypeFilter !== "all" ? "text-blue-600" : "text-gray-400"
                          }`} />
                          <span>Insurance Type</span>
                        </div>
                      </label>
                      <div className="relative">
                        <select
                          value={insuranceTypeFilter}
                          onChange={(e) => setInsuranceTypeFilter(e.target.value)}
                          className="pr-10 pl-4 w-full h-12 text-sm font-medium text-gray-900 bg-gray-50 rounded-xl border-2 border-gray-200 shadow-sm transition-all duration-200 appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white hover:border-gray-300 hover:shadow-md"
                        >
                          <option value="all">All Types</option>
                          <option value="travel_insurance">Travel Insurance</option>
                          <option value="life_insurance">Life Insurance</option>
                          <option value="general_insurance">General Insurance</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2 pointer-events-none" />
                      </div>
                    </div>

                    {/* Status Filter */}
                    <div className="relative group">
                      <label className="block mb-3 text-sm font-semibold text-gray-900">
                        <div className="flex gap-2 items-center">
                          <CheckCircle className={`w-4 h-4 ${
                            statusFilter !== "all" ? "text-emerald-600" : "text-gray-400"
                          }`} />
                          <span>Status</span>
                        </div>
                      </label>
                      <div className="relative">
                        <select
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          className="pr-10 pl-4 w-full h-12 text-sm font-medium text-gray-900 bg-gray-50 rounded-xl border-2 border-gray-200 shadow-sm transition-all duration-200 appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white hover:border-gray-300 hover:shadow-md"
                        >
                          <option value="all">All Status</option>
                          <option value="active">Active</option>
                          <option value="cancelled">Cancelled</option>
                          <option value="cancel_pending">Cancel Pending</option>
                          <option value="pending">Pending</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2 pointer-events-none" />
                      </div>
                    </div>

                    {/* Plan Filter */}
                    <div className="relative group">
                      <label className="block mb-3 text-sm font-semibold text-gray-900">
                        <div className="flex gap-2 items-center">
                          <FileText className={`w-4 h-4 ${
                            planFilter !== "all" ? "text-purple-600" : "text-gray-400"
                          }`} />
                          <span>Plan</span>
                        </div>
                      </label>
                      <div className="relative">
                        <select
                          value={planFilter}
                          onChange={(e) => setPlanFilter(e.target.value)}
                          className="pr-10 pl-4 w-full h-12 text-sm font-medium text-gray-900 bg-gray-50 rounded-xl border-2 border-gray-200 shadow-sm transition-all duration-200 appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white hover:border-gray-300 hover:shadow-md"
                        >
                          <option value="all">All Plans</option>
                          {uniquePlans.map((plan) => (
                            <option key={plan} value={plan}>
                              {plan}
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2 pointer-events-none" />
                      </div>
                    </div>

                    {/* Date Range */}
                    <div className="relative group">
                      <label className="block mb-3 text-sm font-semibold text-gray-900">
                        <div className="flex gap-2 items-center">
                          <CalendarIcon className={`w-4 h-4 ${
                            startDate || endDate ? "text-orange-600" : "text-gray-400"
                          }`} />
                          <span>Date Range</span>
                        </div>
                      </label>
                      <div className="relative">
                        <DatePicker
                          selectsRange={true}
                          startDate={startDate}
                          endDate={endDate}
                          onChange={(update) => setDateRange(update)}
                          placeholderText="Select date range"
                          className="pr-10 pl-4 w-full h-12 text-sm font-medium text-gray-900 bg-gray-50 rounded-xl border-2 border-gray-200 shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white hover:border-gray-300 hover:shadow-md"
                          dateFormat="MMM dd, yyyy"
                        />
                        {(startDate || endDate) && (
                          <button
                            onClick={() => setDateRange([null, null])}
                            className="flex absolute inset-y-0 right-0 z-10 items-center pr-3 text-gray-400 transition-colors hover:text-gray-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Active Filters Badges */}
                  {hasActiveFilters && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="pt-6 mt-8 border-t border-gray-200"
                    >
                      <h4 className="mb-3 text-sm font-semibold text-gray-900">
                        Active Filters
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {globalFilter && (
                          <motion.span
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg"
                          >
                            Search: {globalFilter}
                            <button
                              onClick={() => setGlobalFilter("")}
                              className="transition-colors hover:text-blue-900"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </motion.span>
                        )}
                        {insuranceTypeFilter !== "all" && (
                          <motion.span
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg"
                          >
                            Type: {insuranceTypeFilter.replace("_", " ")}
                            <button
                              onClick={() => setInsuranceTypeFilter("all")}
                              className="transition-colors hover:text-blue-900"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </motion.span>
                        )}
                        {statusFilter !== "all" && (
                          <motion.span
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg"
                          >
                            Status: {statusFilter === "active" ? "Active" : statusFilter.replaceAll("_", " ")}
                            <button
                              onClick={() => setStatusFilter("all")}
                              className="transition-colors hover:text-emerald-900"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </motion.span>
                        )}
                        {planFilter !== "all" && (
                          <motion.span
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg"
                          >
                            Plan: {planFilter}
                            <button
                              onClick={() => setPlanFilter("all")}
                              className="transition-colors hover:text-purple-900"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </motion.span>
                        )}
                        {(startDate || endDate) && (
                          <motion.span
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-lg"
                          >
                            Date: {startDate ? format(startDate, "MMM dd") : ""} - {endDate ? format(endDate, "MMM dd") : ""}
                            <button
                              onClick={() => setDateRange([null, null])}
                              className="transition-colors hover:text-orange-900"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </motion.span>
                        )}
                      </div>
                    </motion.div>
                  )}
                </div>

                {/* Drawer Footer */}
                <div className="p-6 bg-gray-50 border-t border-gray-200">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowFilters(false)}
                    className="py-3 w-full text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl shadow-md transition-all duration-200 hover:from-blue-700 hover:to-indigo-700 hover:shadow-lg"
                  >
                    Apply Filters
                  </motion.button>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm"
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="px-6 py-4 text-sm font-medium text-left text-gray-500"
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <AnimatePresence>
                  {table.getRowModel().rows.map((row, index) => (
                    <motion.tr
                      key={row.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="transition-colors hover:bg-gray-50"
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className="px-6 py-4 whitespace-nowrap"
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center mb-4 space-x-2 lg:mb-0">
                <span className="text-sm text-gray-700">Show</span>
                <select
                  value={table.getState().pagination.pageSize}
                  onChange={(e) => table.setPageSize(Number(e.target.value))}
                  className="px-3 py-1 text-sm rounded border border-gray-300"
                >
                  {[10, 20, 30, 40, 50].map((pageSize) => (
                    <option key={pageSize} value={pageSize}>
                      {pageSize}
                    </option>
                  ))}
                </select>
                <span className="text-sm text-gray-700">
                  of {table.getFilteredRowModel().rows.length} entries
                </span>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => table.setPageIndex(0)}
                  disabled={!table.getCanPreviousPage()}
                  className="p-2 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  <ChevronsLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  className="p-2 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                <span className="px-4 py-2 text-sm text-gray-700">
                  Page {table.getState().pagination.pageIndex + 1} of{" "}
                  {table.getPageCount()}
                </span>

                <button
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  className="p-2 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                  disabled={!table.getCanNextPage()}
                  className="p-2 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  <ChevronsRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <ConfirmCancelModal
        open={cancelAction?.action === "finalize"}
        onClose={() => {
          if (!cancelSubmitting) setCancelAction(null);
        }}
        onConfirm={handleCancelDecision}
        policyId={cancelAction?.id}
        customerName={cancelAction?.customerName}
        isSubmitting={cancelSubmitting}
      />
      <RevertCancelModal
        open={cancelAction?.action === "revert"}
        onClose={() => {
          if (!cancelSubmitting) setCancelAction(null);
        }}
        onConfirm={handleCancelDecision}
        policyId={cancelAction?.id}
        customerName={cancelAction?.customerName}
        isSubmitting={cancelSubmitting}
      />
    </div>
  );
}
